/**
 * geometry.ts - Funciones Geométricas para Validación Cruzada
 * 
 * Módulo extraído de crossValidation.ts (TD-002.G.2)
 * Contiene funciones de distancia y cálculo de centroides simples
 * para coordenadas UTM30 ETRS89 (EPSG:25830).
 * 
 * @created 2026-01-26
 */

import type { UTMPoint } from './types';

// ============================================================================
// FUNCIONES DE DISTANCIA
// ============================================================================

/**
 * Calcula la distancia euclidiana entre dos puntos UTM.
 * 
 * Para coordenadas UTM30 ETRS89 (EPSG:25830), la distancia euclidiana
 * es suficientemente precisa para distancias menores a ~100km.
 * 
 * @param p1 - Primer punto UTM
 * @param p2 - Segundo punto UTM
 * @returns Distancia en metros
 * 
 * @example
 * const d = distanceUTM(
 *   { x: 524538, y: 4229920 },
 *   { x: 524600, y: 4230000 }
 * );
 * // d ≈ 100.5 metros
 */
export function distanceUTM(p1: UTMPoint, p2: UTMPoint): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Verifica si dos puntos están dentro de una tolerancia dada.
 * 
 * @param p1 - Primer punto UTM
 * @param p2 - Segundo punto UTM
 * @param toleranceMeters - Tolerancia máxima en metros
 * @returns true si están dentro de la tolerancia
 * 
 * @example
 * const nearby = pointsWithinTolerance(
 *   { x: 524538, y: 4229920 },
 *   { x: 524540, y: 4229925 },
 *   10
 * );
 * // nearby: true (distancia ~5.4m < 10m)
 */
export function pointsWithinTolerance(
  p1: UTMPoint,
  p2: UTMPoint,
  toleranceMeters: number
): boolean {
  return distanceUTM(p1, p2) <= toleranceMeters;
}

// ============================================================================
// CENTROIDE SIMPLE
// ============================================================================

/**
 * Calcula el centroide simple (media aritmética) de un conjunto de puntos.
 * 
 * Este es el centroide básico sin robustez contra outliers.
 * Para centroide robusto, usar huberCentroid de statistics.ts.
 * 
 * @param points - Array de puntos UTM (mínimo 1)
 * @returns Centroide simple
 * @throws Error si el array está vacío
 * 
 * @example
 * const center = simpleCentroid([
 *   { x: 524538, y: 4229920 },
 *   { x: 524540, y: 4229925 },
 *   { x: 524542, y: 4229930 }
 * ]);
 * // center: { x: 524540, y: 4229925 }
 */
export function simpleCentroid(points: UTMPoint[]): UTMPoint {
  if (points.length === 0) {
    throw new Error('No se puede calcular centroide de array vacío');
  }
  
  if (points.length === 1) {
    return { ...points[0] };
  }
  
  const sumX = points.reduce((sum, p) => sum + p.x, 0);
  const sumY = points.reduce((sum, p) => sum + p.y, 0);
  
  return {
    x: sumX / points.length,
    y: sumY / points.length
  };
}
